import type { ProposedDocument, StoredSections } from '../types'
import { ExtraDocumentLinkTypes } from '../types'
import type {
  AIOCRProposedDocuments,
  DocumentUploaderItem,
} from '@app/shared/types/sf-data'

import humps from 'humps'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getEffectiveId } from '../utils'

export const useOCRSections = defineStore('OCRSections', () => {
  const activeSection = ref<string | null>(null)
  const sections = ref<StoredSections>({})
  const sectionNames = ref<Record<string, string>>({})
  const sectionOrder = ref<string[]>([])

  const sectionsPerDocument = computed(() => {
    return Object.keys(sections.value).reduce((acc, section) => {
      const sectionDocumentIds = sections.value[section]

      sectionDocumentIds.forEach((documentId) => {
        acc[documentId] = section
      })

      return acc
    }, {} as Record<string, string>)
  })

  function activateSection(section: string): void {
    activeSection.value = section
  }

  /**
   * Based on a list of documents, generate the respective sections and add the document ids to them.
   * @param incomingDocuments - The documents to add to the sections
   */
  function addSectionsPerDocuments(
    incomingDocuments: DocumentUploaderItem[]
  ): void {
    incomingDocuments.forEach((doc) => {
      if (!activeSection.value) {
        return
      }

      const effectiveId = getEffectiveId(doc)

      if (!effectiveId) {
        return
      }

      const section = sections.value[activeSection.value] || []

      if (!section.includes(effectiveId)) {
        sections.value = {
          ...sections.value,
          [activeSection.value]: [...section, effectiveId],
        }
      }
    })
  }

  /**
   * Deletes a document from a section. If the document is not in any other section, it can be deleted from the store entirely.
   * @param documentId - The ID of the document to delete from the section
   * @returns - A boolean indicating whether the document can be deleted from the store entirely
   */
  function deleteDocumentFromSection(documentId: number | string): boolean {
    const section = sectionsPerDocument.value[documentId]

    if (!section) {
      return false
    }

    const sectionDocumentIds = sections.value[section]

    const documentIdString = documentId.toString()

    sections.value = {
      ...sections.value,
      [section]: sectionDocumentIds.filter((id) => id !== documentIdString),
    }

    const otherSections = Object.keys(sections.value).filter(
      (s) => s !== section
    )

    const doesItExistInOtherSections = otherSections.some((s) =>
      sections.value[s].includes(documentIdString)
    )

    return !doesItExistInOtherSections
  }

  /**
   * A function to receive the raw data from the server, containing the proposed documents per section.
   * The function will normalize the data and return the documents in a format that can be used by other stores down the line.
   * @param incomingDocumentsPerSection - The raw data from the server
   * @returns - The normalized documents
   */
  function receiveSections(
    incomingDocumentsPerSection: AIOCRProposedDocuments
  ): Record<string, ProposedDocument> {
    const documentsPerSection = humps.camelizeKeys(
      incomingDocumentsPerSection
    ) as AIOCRProposedDocuments

    const sectionKeys = Object.keys(documentsPerSection)

    if (sectionKeys.length === 0) {
      return {}
    }

    const {
      documents: normalizedDocuments,
      sections: normalizedSections,
      sectionOrder: incomingSectionOrder,
      sectionNames: incomingSectionNames,
    } = sectionKeys.reduce(
      (acc, section) => {
        const documents = documentsPerSection[section].documents

        const { documentIds, normalizedDocuments } = documents.reduce(
          (docAcc, document) => {
            const { documentId, documentName } = document || {}

            if (!documentId || !documentName) {
              return docAcc
            }

            docAcc.documentIds.push(documentId.toString())

            docAcc.normalizedDocuments[documentId] = Object.freeze({
              id: documentId,
              name: documentName,
              linkType: ExtraDocumentLinkTypes.ProposedDocument,
            })

            return docAcc
          },
          {
            documentIds: [] as string[],
            normalizedDocuments: {} as Record<string, ProposedDocument>,
          }
        )

        acc.sections = {
          ...acc.sections,
          [section]: documentIds,
        }

        acc.documents = {
          ...acc.documents,
          ...normalizedDocuments,
        }

        acc.sectionOrder.push(section)

        acc.sectionNames[section] = documentsPerSection[section].section

        return acc
      },
      {
        documents: {} as Record<string, ProposedDocument>,
        sections: {} as Record<string, string[]>,
        sectionNames: {} as Record<string, string>,
        sectionOrder: [] as string[],
      }
    )

    sections.value = normalizedSections
    sectionOrder.value = incomingSectionOrder
    sectionNames.value = incomingSectionNames

    return normalizedDocuments
  }

  return {
    activeSection,
    activateSection,
    addSectionsPerDocuments,
    deleteDocumentFromSection,
    receiveSections,
    sections,
    sectionNames,
    sectionOrder,
  }
})
